import {useEffect} from 'react';
import posthog, {CaptureResult} from 'posthog-js';
import {useTypedAppSelector} from 'redux/hooks';
import {redactSensitiveUrlParams} from 'utils/sensitiveUrl';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
// Lets us filter events by deploy target in PostHog insights (e.g. `environment does not equal prod`).
// Set explicitly per target via VITE_POSTHOG_ENVIRONMENT ('local' | 'dev' | 'stage' | 'prod') -
// each build target sets its own value (see .env.development/.env.production and CI build args).
const POSTHOG_ENVIRONMENT = import.meta.env.VITE_POSTHOG_ENVIRONMENT || 'local';

// posthog.reset() (called on logout/no-profile below) wipes previously registered super
// properties along with the distinct_id, so `environment` must be re-applied after every reset.
const registerEnvironment = () => posthog.register({environment: POSTHOG_ENVIRONMENT});

// Runs on every event before it's sent to PostHog. Redacts sensitive query params from any
// URL-shaped property ($current_url, $referrer, etc.) so one-time tokens never leave the browser.
const redactSensitiveUrlProperties = (event: CaptureResult | null): CaptureResult | null => {
  if (!event?.properties) return event;
  Object.keys(event.properties).forEach(key => {
    if ((key.includes('url') || key.includes('href')) && typeof event.properties[key] === 'string') {
      event.properties[key] = redactSensitiveUrlParams(event.properties[key]);
    }
  });
  return event;
};

if (POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: 'history_change',
    loaded: () => registerEnvironment(),
    before_send: redactSensitiveUrlProperties,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
    },
  });
} else if (import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.warn('PostHog disabled: VITE_POSTHOG_KEY is not set');
}

const PosthogIdentifyUser = () => {
  const profile = useTypedAppSelector(state => state.user.profile);
  const isProfileLoading = useTypedAppSelector(state => state.user.isProfileLoading);
  const isImpersonating = !!profile?.impersonation?.active;
  useEffect(() => {
    if (isProfileLoading) return;
    // Don't identify as the user being impersonated: the events come from a staff member
    // clicking around, and attributing them to that person's PostHog identity would corrupt
    // their history and session recordings.
    if (isImpersonating) {
      posthog.reset();
      registerEnvironment();
      return;
    }
    if (profile?.username) {
      posthog.identify(profile.username, {
        email: profile.email,
        first_name: profile.first_name,
        last_name: profile.last_name,
        is_staff: profile.is_staff,
      });
    } else {
      posthog.reset();
      registerEnvironment();
    }
  }, [
    profile?.username,
    profile?.email,
    profile?.first_name,
    profile?.last_name,
    profile?.is_staff,
    isProfileLoading,
    isImpersonating,
  ]);
  return null;
};

export {posthog, PosthogIdentifyUser};
